/* eslint-disable import/no-duplicates */
import { Editor } from '@tinymce/tinymce-react'
import CX from 'classnames'
import React, { useState, useRef } from 'react'
import 'tinymce'
import 'tinymce/plugins/paste'
import 'tinymce/themes/modern/theme'
import 'tinymce/tinymce'
import Client from '../../client'
import { LoadableSelect } from '../Inputs'
/* eslint-enable import/no-duplicates */

interface RichEditorProps {
  value: string
  onChange: (change: string) => void
  editorHeight?: number
  disabled?: boolean
  showTemplates?: boolean
  showTemplatesPosition?: string
  style?: React.CSSProperties
}

const RichEditorExample = (props: RichEditorProps) => {
  const { value, onChange, disabled } = props
  const { showTemplates, editorHeight, ...domFriendlyProps } = props
  const [templateValue, setTemplateValue] = useState<number>(-1)
  const [focused, setFocused] = useState<boolean>(false)
  const [lastFocusOnEditor, setLastFocusOnEditor] = useState<boolean>(false)
  const editorRef = useRef(null)

  const onSelectTemplate = async v => {
    const res = await Client.getTemplate(v)
    const { body } = res.data.template
    if (lastFocusOnEditor) {
      // Getting tinymce to take a newline after inserting the text is kinda funky
      // Need to do this and still it's not great.
      // '\n' does not work. Tinymce eats it for some reason.
      editorRef.current?.insertContent(body, { format: 'html' })
    } else {
      const newVal = value === undefined || value === null ? body : `${value}<p>${body}</p>`
      onChange(newVal)

      // Scroll to bottom after adding text at the bottom
      editorRef.current?.getWin()?.scrollTo(0, editorRef.current.getBody().scrollHeight)
    }

    setTemplateValue(v)
    analytics.track('Static Template Inserted')
  }

  const renderTemplates = () => {
    let containerStyle
    if (props.showTemplatesPosition === 'bottom') {
      // express list
      containerStyle = { position: 'absolute', bottom: '8px', right: '16px', width: '50%' }
    } else {
      // classic list
      containerStyle = { position: 'absolute', top: '5px', right: '7px', width: '30%' }
    }

    return (
      <div style={containerStyle}>
        <LoadableSelect
          value={templateValue}
          onChange={v => onSelectTemplate(v)}
          clearable={false}
          load={() => Client.templateIndex({ draft: false, static: true })}
          autoload
          noLabel={props.showTemplatesPosition === 'bottom'}
          placeholder="Insert Template"
          process={d => d.templates.map(s => ({ value: s.id, label: s.title }))}
          disabled={disabled}
          onFocus={() => {
            if (focused) {
              setLastFocusOnEditor(true)
            }
          }}
          onBlur={() => {
            setLastFocusOnEditor(false)
          }}
        />
      </div>
    )
  }

  return (
    <div
      className={CX('wrapper', { templates: showTemplates })}
      style={{ position: 'relative', marginTop: '5px' }}
      {...domFriendlyProps}
      onChange={undefined}
    >
      <Editor
        value={value}
        disabled={disabled}
        init={{
          plugins: 'paste',
          skin_url: '/tinymce/skins',
          extended_valid_elements: 'strong,b,em,i,u,p,br,ul,li,ol,sub,sup,del,strike,href',
          menubar: false,
          browser_spellcheck: true,
          toolbar: 'undo redo | bold italic | alignleft aligncenter alignright',
          height: editorHeight || 300
        }}
        onInit={(event, editor) => {
          editorRef.current = editor

          // This whole flow that includes the focused variable and the lastFocusOnEditor
          // variable is to handle the switch in focus to the dropdown but being able to know
          // that the editor was the last thing focused before it switched to the dropdown.
          // This is so that we handle the different behaviours when adding a template.
          // i.e. editor in focus (actually editor was last in focus since the focus now is
          // on the dropdown) and editor not in focus.
          editor.on('focusout', () => {
            setFocused(false)
          })
          editor.on('focusin', () => {
            setFocused(true)
          })
        }}
        onEditorChange={e => {
          onChange(e)
        }}
      />
      {showTemplates && renderTemplates()}
    </div>
  )
}

export default RichEditorExample
